<script setup lang="ts">
import { useIntersectionObserver } from "@vueuse/core";
import {
  type MediaContent,
  type ProductDetails,
  SalesMethod,
} from "mws-ts-v2-sdk";

import { emptyGuid } from "~/constants/misc";
import { useEventQuery } from "~/queries/catalog/events";
import { MediaContentType } from "~/types/Catalog";

const { product } = defineProps<{
  product: ProductDetails;
}>();

const productStarted = computed(() => {
  return product.startDate <= new Date();
});

const eventId = computed(() => product.eventId);
const isEventQueryEnabled = computed(
  () => !!product.eventId && product.eventId !== emptyGuid,
);
const { data: event, suspense } = useEventQuery(eventId, {
  enabled: isEventQueryEnabled,
});

const { selectedPreset } = useProvideProductDetailStore();

onServerPrefetch(async () => {
  if (isEventQueryEnabled.value) {
    await suspense();
  }
});

const productMedia = computed(() => {
  let media: MediaContent[] = [];
  if (
    product.salesMethod === SalesMethod.FixedPrice &&
    selectedPreset.value?.imageUrl
  ) {
    media = [
      {
        type: MediaContentType.Image,
        url: selectedPreset.value.imageUrl,
        metadata: null,
      } satisfies MediaContent,
      ...(product?.media.slice(1) ?? []),
    ];
  } else {
    media = product.media.concat(product.gallery) ?? [];
  }

  return media;
});

const isBidProductSidebarVisible = ref(true);
const productBidSidebar = useTemplateRef("productBidSidebar");
useIntersectionObserver(productBidSidebar, (entries) => {
  isBidProductSidebarVisible.value = entries?.[0]?.isIntersecting || false;
});

const { baseHeaderBottom } = useBaseHeaderBounding();
</script>

<template>
  <div
    class="max-md:browser-viewport-span md:site-viewport-container pt-2 md:pt-8"
  >
    <LayoutReturnToPartnerButton
      :product-id="product.id"
      class="w-fit mb-2"
    />

    <div class="md:flex md:justify-between gap-12 lg:gap-20 xl:gap-32">
      <div class="w-full md:max-w-1/2 lg:max-w-full">
        <div
          v-if="event"
          class="md:hidden px-4 md:px-0"
        >
          <ProductPageHeader
            :product="product"
            :event="event"
          />
        </div>
        <ProductMediaGallery
          :media="productMedia"
          :product="product"
        />
        <ProductDescription
          class="hidden md:flex"
          :product="product"
          data-test="product-description"
        />
      </div>

      <div
        class="md:mb-2 w-full md:w-1/4 min-w-80 xl:w-1/3 xl:min-w-96 2xl:w-1/2 2xl:min-w-112"
        data-cy="main-container-sidebar"
        data-test="product-sidebar"
      >
        <BaseSkeletonLoader
          v-if="isEventQueryEnabled && !event"
          width="100%"
          :height="600"
          class="border"
        />

        <template
          v-else-if="
            product.salesMethod === SalesMethod.Bidding &&
            product.availableQuickBidPrices &&
            event
          "
        >
          <ProductSidebar
            ref="productBidSidebar"
            data-cy="main-bid-product-sidebar"
            :product="product"
            :event="event"
            :product-started="productStarted"
          />
          <Transition>
            <div
              v-if="!isBidProductSidebarVisible"
              class="hidden md:block sticky top-0 sticky-transition"
              :style="{
                paddingTop: `${baseHeaderBottom + 16}px`,
              }"
            >
              <ProductSidebarTopSection
                :product="product"
                :event="event"
                :product-started="productStarted"
              />

              <div class="border-t mt-6 py-6">
                <EngagementTrustPilotWidget
                  class="scale-90"
                  variant="micro-star"
                />
              </div>
            </div>
          </Transition>
        </template>

        <LazyProductSidebarFixedPriceProduct
          v-else-if="product.salesMethod === SalesMethod.FixedPrice"
          :product="product"
        />
      </div>

      <div class="block md:hidden">
        <ProductDescription
          data-cy="product-description-drawer"
          class="md:hidden"
          :product="product"
          :product-started="productStarted"
        />
      </div>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.main-container {
  &__left {
    @apply flex-col max-w-full md:max-w-1/2 lg:max-w-none flex-grow;
    flex: 3;
    flex-grow: 3;
  }
}
.sticky-transition {
  transition: padding-top 0.3s ease;
}
.v-enter-active {
  transition: opacity 0.2s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
